import { useEffect, useState } from "react";
import { Layers, Coins, ChevronDown, Ruler } from "lucide-react";
import api from "../lib/api";
import { usd } from "../lib/money";
import { useAuth } from "../context/AuthContext";
import { ADMIN_ROLES, PaintWarningsList, useAdminPaintWarnings } from "./PaintWarnings";

const SCULPTORS = {
  hunyuan3d_pro: "Hunyuan3D 3.1 Pro", hunyuan3d: "Hunyuan3D", trellis: "Trellis", "3daistudio": "Tripo 3.2 (3D AI Studio)",
  rodin: "Rodin", "rodin_v2.5": "Rodin v2.5", "rodin_v2.5_highpack": "Rodin v2.5 HighPack", "meshy_7.1": "Meshy 7.1", meshy: "Meshy",
};

// Collapse state lives in localStorage, behind a try/catch so a locked-down browser (private mode, storage disabled)
// still works — it just falls back to in-memory state for the session.
const readCollapsed = (key, fallback) => {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
};
const writeCollapsed = (key, val) => {
  try {
    window.localStorage.setItem(key, val ? "1" : "0");
  } catch {
    /* storage disabled — keep the choice in memory only */
  }
};
const useCollapse = (key, defaultCollapsed = true) => {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(key, defaultCollapsed));
  const toggle = () =>
    setCollapsed((c) => {
      const next = !c;
      writeCollapsed(key, next);
      return next;
    });
  return [collapsed, toggle];
};

const SectionHeader = ({ testId, expanded, onToggle, icon: Icon, children }) => (
  <button
    type="button"
    data-testid={testId}
    aria-expanded={expanded}
    onClick={onToggle}
    className="flex w-full items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
  >
    <Icon className="h-4 w-4" />
    {children}
    <ChevronDown className={`ml-auto h-4 w-4 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
  </button>
);

const Row = ({ label, testId, children }) => (
  <>
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="font-semibold text-right font-mono2" data-testid={testId}>{children}</dd>
  </>
);

// "$0.021" under $1 (3 decimals), "$1.20" from $1 (2 decimals); no cost = "$0.000".
export const costUsd = (v) => {
  const n = Number(v) > 0 ? Number(v) : 0;
  return usd(n, n >= 1 ? 2 : 3);
};

// One cost line: the name (+ a lighter "(N×)" for 2+ calls) truncates; the cost stays right-aligned on one line.
// `tagSlot`: some row is estimated, so every row keeps the "est." width and the amounts stay in one column.
const CostLine = ({ label, calls, amount, estimated, tagSlot, testId, total = false }) => (
  <div className={`flex items-baseline gap-3 ${total ? "pt-1 border-t border-border/70" : ""}`}>
    <dt className={`min-w-0 flex-1 truncate ${total ? "font-semibold" : "text-muted-foreground"}`} data-testid={`${testId}-name`}>
      {label}
      {calls > 1 && <span className="ml-1.5 text-xs text-muted-foreground/60">({calls}×)</span>}
    </dt>
    <dd className={`shrink-0 whitespace-nowrap text-right font-mono2 tabular-nums ${total ? "font-bold text-accent" : "font-semibold"}`} data-testid={testId}>
      {costUsd(amount)}
      {tagSlot && <span className="ml-1 inline-block w-6 text-left text-[10px] font-normal text-muted-foreground">{estimated ? "est." : ""}</span>}
    </dd>
  </div>
);

// Spools a 4/8/12-slot build merged into the kept ones, e.g. "Hot Pink → Pumpkin Orange 10%"; eye parts the 4-slot
// eye convention moved carry their look, e.g. "Eye whites → Black 0.6% (solid dark eyes)". Admins also see the spools
// that paint nothing (left out of the 3MF).
const foldPct = (s) => (s < 0.0005 ? "<0.1%" : `${(100 * s).toFixed(100 * s >= 10 ? 0 : 1)}%`);
// Admins only: read from the admin route, never part of the duck data a customer's page gets.
const useAdminEmptySlots = (creation) => {
  const { user } = useAuth();
  const admin = ADMIN_ROLES.includes(user?.role);
  const [empty, setEmpty] = useState([]);
  useEffect(() => {
    if (!admin || !creation.id) return undefined;
    let live = true;
    api.get(`/dash/ducks/${creation.id}/empty-slots`)
      .then((r) => live && setEmpty(r.data?.empty_slots || []))
      .catch(() => live && setEmpty([]));
    return () => { live = false; };
  }, [admin, creation.id, creation.version, creation.palette?.length]);
  return admin ? empty : [];
};
export const EmptySlots = ({ empty, testId = "print-empty-slots" }) => (
  <span data-testid={testId}>
    <span className="font-semibold text-foreground">Left out of the 3MF (paints nothing):</span>{" "}
    {empty.map((e) => e.name || e.hex).join(", ")}
  </span>
);
const FoldNote = ({ folds, slots, empty }) => {
  if (!folds?.length && !empty?.length) return null;
  return (
    <p data-testid="print-fold-note" className="mt-3 text-xs text-muted-foreground">
      {folds?.length > 0 && <span className="font-semibold text-foreground">Fitted to {slots || "fewer"} slots:</span>}{" "}
      {(folds || []).map((f, i) => (
        <span key={`${i}-${f.from}-${f.to}`} data-testid={`print-fold-${i}`}>
          {i > 0 && " · "}
          {f.from} → {f.to} {foldPct(f.share)}
          {f.eyes ? ` (${f.eyes})` : ""}
          {f.standin ? ` ${f.standin}` : ""}
        </span>
      ))}
      {folds?.length > 0 && empty?.length > 0 && " · "}
      {empty?.length > 0 && <EmptySlots empty={empty} />}
    </p>
  );
};

// Faces the unclaimed-face fill couldn't reach print in the body colour: always a warning (a 30+ face patch also holds ordering).
const UnclaimedNote = ({ health }) => {
  if (!health?.unclaimed_left) return null;
  return (
    <p data-testid="print-unclaimed-note" className="mt-3 text-xs text-amber-700 dark:text-amber-400">
      {health.unclaimed_left} {health.unclaimed_left === 1 ? "face" : "faces"} no colour region claimed will print in the
      body colour (largest patch {health.unclaimed_largest} {health.unclaimed_largest === 1 ? "face" : "faces"}).
    </p>
  );
};

// Step 3: a render color without a matching filament. Everyone reads one plain line (the ordering hold is the attention
// card's job); admins / owners also get the per-color detail from the admin route.
const MissingColorNote = ({ creation }) => {
  const fidelity = creation.paint_fidelity;
  const flagged = fidelity?.ok === false && !!fidelity.reason;
  const warnings = useAdminPaintWarnings(creation, flagged);
  if (!flagged) return null;
  return (
    <div data-testid="print-missing-color-note" className="mt-3 text-xs text-amber-700 dark:text-amber-400">
      <p data-testid="print-missing-color-reason">{fidelity.reason}</p>
      <PaintWarningsList warnings={warnings} testId="print-paint-warnings" />
    </div>
  );
};

// Owner-only AI spend per pipeline phase (ledger when tracked, "est." for ducks older than the ledger).
const CostRows = ({ creationId }) => {
  const [cost, setCost] = useState(null);
  const [collapsed, toggle] = useCollapse("pp-cost-collapsed", true);
  useEffect(() => {
    api.get(`/creations/${creationId}/cost`).then((r) => setCost(r.data)).catch(() => setCost(null));
  }, [creationId]);
  if (!cost) return null;
  const tagSlot = !!cost.estimated || cost.phases.some((p) => p.estimated);
  return (
    <div data-testid="print-cost" className="mt-5 pt-4 border-t border-border/70">
      <div className="flex items-center gap-2 mb-3">
        <SectionHeader testId="print-cost-toggle" expanded={!collapsed} onToggle={toggle} icon={Coins}>
          AI cost per phase
        </SectionHeader>
        {cost.estimated && (
          <span data-testid="print-cost-estimated" className="rounded-full bg-muted px-2 py-0.5 text-[10px] normal-case tracking-normal" title="Older duck — estimated from the price table, not a tracked ledger">
            est.
          </span>
        )}
      </div>
      {!collapsed && (
        <>
          <dl className="space-y-2 text-sm">
            {cost.phases.map((p) => (
              <CostLine key={p.name} label={p.label} calls={p.calls} amount={p.usd} estimated={p.estimated} tagSlot={tagSlot} testId={`print-cost-${p.name}`} />
            ))}
            <CostLine label="Total" amount={cost.total} estimated={cost.estimated} tagSlot={tagSlot} testId="print-cost-total" total />
          </dl>
          <p className="mt-2 text-[11px] text-muted-foreground">Gemini images at the Flash rate; the Pro fallback (rare) bills higher. Trim & package run locally.</p>
        </>
      )}
    </div>
  );
};

// Phase 1 edge-quality metrics for this build (tiny regions, detached shells, boundary edges, open/non-manifold edges).
const EdgeRows = ({ metrics }) => {
  const [collapsed, toggle] = useCollapse("pp-edge-collapsed", true);
  if (!metrics) return null;
  const pairs = Object.entries(metrics.interface_edges || {}).slice(0, 3);
  return (
    <div data-testid="print-edge" className="mt-5 pt-4 border-t border-border/70">
      <SectionHeader testId="print-edge-toggle" expanded={!collapsed} onToggle={toggle} icon={Ruler}>
        Edge quality
      </SectionHeader>
      {!collapsed && (
        <dl className="grid grid-cols-2 gap-y-2 text-sm mt-3">
          <Row label="Faces" testId="edge-faces">{(metrics.faces || 0).toLocaleString()}</Row>
          <Row label="Detached shells" testId="edge-shells">
            {metrics.detached_shells ?? 0}{metrics.shells_removed ? ` · ${metrics.shells_removed} removed` : ""}
          </Row>
          <Row label="Tiny regions (<30)" testId="edge-tiny">{metrics.tiny_regions ?? 0}</Row>
          <Row label="Open edges" testId="edge-open">{metrics.open_edges ?? 0}</Row>
          <Row label="Non-manifold" testId="edge-nonmanifold">{metrics.nonmanifold_edges ?? 0}</Row>
          {pairs.map(([k, v]) => (
            <Row key={k} label={`Border ${k}`} testId={`edge-interface-${k}`}>{Number(v).toLocaleString()}</Row>
          ))}
        </dl>
      )}
    </div>
  );
};

// Print facts for the sculpted duck (height, sculptor, mesh size, base, views, layer height, supports) + per-phase AI cost.
export const PrintProfileCard = ({ creation, height, qrCheck }) => {
  const views = creation.views_used?.length ? creation.views_used : ["back"];
  const palette = creation.palette || [];
  const baseSlot = creation.print_options?.base_slot ?? qrCheck?.base_slot;
  const [collapsed, toggle] = useCollapse("pp-facts-collapsed", true);
  const empty = useAdminEmptySlots(creation);
  return (
    <div className="rounded-3xl border border-border bg-card/60 p-5">
      <div className="mb-4">
        <SectionHeader testId="print-profile-toggle" expanded={!collapsed} onToggle={toggle} icon={Layers}>
          Print profile
        </SectionHeader>
        <FoldNote folds={creation.paint_folds} slots={creation.paint_opts?.colors} empty={empty} />
        <UnclaimedNote health={creation.paint_health} />
        <MissingColorNote creation={creation} />
      </div>
      {!collapsed && (
        <>
          <dl className="grid grid-cols-2 gap-y-3 text-sm">
            <Row label="Height" testId="print-height">{height} mm</Row>
            <Row label="Sculptor" testId="print-sculptor">{SCULPTORS[creation.sculptor] || creation.sculptor || "Trellis"}</Row>
            <Row label="Mesh" testId="print-faces">{creation.mesh_faces ? `${creation.mesh_faces.toLocaleString()} tris` : "—"}</Row>
            <Row label="Base" testId="print-base">{creation.flat_base ? "Flat, trimmed" : "As sculpted"}</Row>
            <Row label="Views" testId="print-views">{creation.multi_view ? `Front + AI ${views.join("/")}` : "Front only"}</Row>
            <Row label="Layer height">0.12–0.20 mm</Row>
            <Row label="Supports" testId="print-supports">Auto (tree slim) · 0.3 mm top Z</Row>
            <Row label="Base colour" testId="print-base-colour">
              {palette[baseSlot] ? `${palette[baseSlot].name || palette[baseSlot].hex} (one colour)` : "Dominant (one colour)"}
            </Row>
            <Row label="Bottom QR" testId="print-qr">
              {!qrCheck ? "—" : qrCheck.state === "on" ? `Printed · ${qrCheck.module_mm} mm modules` : qrCheck.state === "skipped" ? "Not printed — base too small" : "Switched off for this export"}
            </Row>
          </dl>
          {qrCheck?.notice && (
            <p data-testid="print-qr-reason" className="mt-3 text-xs text-muted-foreground">{qrCheck.notice}</p>
          )}
        </>
      )}
      <CostRows creationId={creation.id} />
      <EdgeRows metrics={creation.edge_metrics} />
    </div>
  );
};
